/**
 * SystemAdminPage - System Administration Panel for Tenant Admins
 * 
 * This is the shell/layout component. All panels are imported from
 * ./SystemAdmin/panels/ for maintainability.
 * 
 * Accessible only to users with is_admin=true
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import AppShell from '../components/layout/AppShell';
import { EmptyState } from '../components/layout/SharedComponents';
import {
  Settings, Users, Briefcase, UserCheck, ChevronRight,
  FileText, ShoppingCart, ClipboardList, Bell, Database, HardDrive,
  Building, Activity, DollarSign, Package, Receipt, UtensilsCrossed, Landmark, ScrollText, Shield, Brain, Tag, ExternalLink, UsersRound,
} from 'lucide-react';

// Panel imports
import { UserRegisterPanel } from './SystemAdmin/panels/UserPanels';
import { PurchasingManagerPanel } from './SystemAdmin/panels/PurchasingManagerPanel';
import { JobTitlesPanel } from './SystemAdmin/panels/JobTitlesPanel';
import { UserGroupsPanel } from './SystemAdmin/panels/UserGroupsPanel';
import { RequestsSettingsPanel } from './SystemAdmin/panels/RequestsSettingsPanel';
import { OrdersSettingsPanel } from './SystemAdmin/panels/OrdersSettingsPanel';
import { RFQSettingsPanel } from './SystemAdmin/panels/RFQSettingsPanel';
import { InvoicePaymentSettingsPanel } from './SystemAdmin/panels/InvoicePaymentSettingsPanel';
import { EnvironmentSettingsPanel } from './SystemAdmin/panels/EnvironmentSettingsPanel';
import { SupplierManagementPanel } from './SystemAdmin/panels/SupplierManagementPanel';
import { InventoryManagementPanel } from './SystemAdmin/panels/InventoryManagementPanel';
import { SystemNotificationsPanel } from './SystemAdmin/panels/SystemNotificationsPanel';
import { DataManagementPanel } from './SystemAdmin/panels/DataManagementPanel';
import { ExpenseManagementPanel } from './SystemAdmin/panels/ExpenseManagementPanel';
import { ItemRecipePanel } from './SystemAdmin/panels/ItemRecipePanel';
import { SitesPanel } from './SystemAdmin/panels/SitesPanel';
import { AuditLogsPanel } from './SystemAdmin/panels/AuditLogsPanel';
import { ContractTemplatesPanel } from './SystemAdmin/panels/ContractTemplatesPanel';
import { ContractReviewSettingsPanel } from './SystemAdmin/panels/ContractReviewSettingsPanel';
import { GoodsReceivedSettingsPanel } from './SystemAdmin/panels/GoodsReceivedSettingsPanel';
import { InvoiceSettingsPanel } from './SystemAdmin/panels/InvoiceSettingsPanel';
import { ExternalURLsPanel } from './SystemAdmin/panels/ExternalURLsPanel';
import { RolesPanel } from './SystemAdmin/panels/RolesPanel';
import AIBillingPanel from './SystemAdmin/panels/AIBillingPanel';
import VersionHistoryPanel from './SystemAdmin/panels/VersionHistoryPanel';
import SystemBackupsPanel from './SystemAdmin/panels/SystemBackupsPanel';
import {
  SetSystemApprovalRulesPanel,
} from './SystemAdmin/panels/ApprovalPanels';

// Admin menu sections
const ADMIN_SECTIONS = [
  {
    id: 'users',
    label: 'User Maintenance',
    icon: Users,
    subItems: [
      { id: 'user-register', label: 'User Register' },
      { id: 'purchasing-manager', label: 'Purchasing Manager' },
      { id: 'job-titles', label: 'Job Title' },
    ]
  },
  { id: 'roles', label: 'Permission Roles', icon: Shield, subItems: [] },
  { id: 'user-groups', label: 'User Groups', icon: UsersRound, subItems: [] },
  { id: 'requests', label: 'Requests', icon: ClipboardList, subItems: [], module: 'requisitions' },
  { id: 'orders', label: 'Orders', icon: ShoppingCart, subItems: [], module: 'orders' },
  { id: 'rfq', label: 'RFx Sourcing', icon: FileText, subItems: [], module: 'rfq' },
  { id: 'approvals', label: 'System Approval', icon: UserCheck, subItems: [
    { id: 'set-system-approval-rules', label: 'Set System Approval Rules' },
  ] },
  { id: 'contracts', label: 'Contracts', icon: ScrollText, subItems: [
    { id: 'contract-templates', label: 'Contract Templates' },
    { id: 'contract-settings', label: 'Contract Review Settings' },
  ], module: 'contracts' },
  { id: 'invoice-payment', label: 'Invoice and Payment', icon: DollarSign, subItems: [], module: 'invoicing' },
  { id: 'environment', label: 'Environment Settings', icon: Settings, subItems: [] },
  { id: 'suppliers', label: 'Supplier Management', icon: Building, subItems: [], module: 'supplier_portal' },
  { id: 'inventory', label: 'Inventory Management', icon: Package, subItems: [], module: 'inventory' },
  { id: 'goods-received', label: 'Receiving', icon: Package, subItems: [], module: 'goods_receiving' },
  { id: 'notifications', label: 'System Notifications', icon: Bell, subItems: [] },
  { id: 'data', label: 'Data Management', icon: Database, subItems: [] },
  { id: 'expenses', label: 'Expense Management', icon: Receipt, subItems: [], module: 'expenses' },
  { id: 'item-recipe', label: 'Item Recipe', icon: UtensilsCrossed, subItems: [], module: 'recipe' },
  { id: 'sites', label: 'Sites', icon: Landmark, subItems: [], bold: true, module: 'sites' },
  { id: 'audit', label: 'Audit Logs', icon: Activity, subItems: [] },
  { id: 'ai-billing', label: 'AI Services & Billing', icon: Brain, subItems: [] },
  { id: 'backups', label: 'System Backups', icon: HardDrive, subItems: [] },
  { id: 'version-history', label: 'Version History', icon: Tag, subItems: [] },
];

// Map section/subItem IDs to panel components
const PANEL_MAP = {
  // Sub-item panels
  'user-register': UserRegisterPanel,
  'purchasing-manager': PurchasingManagerPanel,
  'job-titles': JobTitlesPanel,
  'set-system-approval-rules': SetSystemApprovalRulesPanel,
  'contract-templates': ContractTemplatesPanel,
  'contract-settings': ContractReviewSettingsPanel,
  'goods-received': GoodsReceivedSettingsPanel,
};

const SECTION_MAP = {
  'roles': RolesPanel,
  'user-groups': UserGroupsPanel,
  'requests': RequestsSettingsPanel,
  'orders': OrdersSettingsPanel,
  'rfq': RFQSettingsPanel,
  'contracts': ContractTemplatesPanel,
  'contract-templates': ContractTemplatesPanel,
  'contract-settings': ContractReviewSettingsPanel,
  'invoice-payment': InvoicePaymentSettingsPanel,
  'environment': EnvironmentSettingsPanel,
  'external-urls': ExternalURLsPanel,
  'suppliers': SupplierManagementPanel,
  'inventory': InventoryManagementPanel,
  'goods-received': GoodsReceivedSettingsPanel,
  'notifications': SystemNotificationsPanel,
  'data': DataManagementPanel,
  'expenses': ExpenseManagementPanel,
  'item-recipe': ItemRecipePanel,
  'sites': SitesPanel,
  'audit': AuditLogsPanel,
  'ai-billing': AIBillingPanel,
  'backups': SystemBackupsPanel,
  'version-history': VersionHistoryPanel,
};

export default function SystemAdminPage() {
  const { user, systemSettings } = useAuth();
  const [activeSection, setActiveSection] = useState('users');
  const [activeSubItem, setActiveSubItem] = useState('user-register');
  const [expandedSections, setExpandedSections] = useState(['users']);
  const [regRequestCount, setRegRequestCount] = useState(0);

  useEffect(() => {
    api.get('/api/auth/registration-requests').then(r => {
      setRegRequestCount((r.data || []).filter(req => req.status === 'pending').length);
    }).catch(() => {});
  }, [activeSubItem]);
  
  if (!user?.is_admin) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[60vh]">
          <EmptyState title="Access Denied" description="You need System Administrator privileges to access this section." />
        </div>
      </AppShell>
    );
  }

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => prev.includes(sectionId) ? prev.filter(s => s !== sectionId) : [...prev, sectionId]);
  };

  const handleSubItemClick = (sectionId, subItemId) => {
    setActiveSection(sectionId);
    setActiveSubItem(subItemId);
  };

  // Determine which panel to render
  const SubPanel = activeSubItem ? PANEL_MAP[activeSubItem] : null;
  const SectionPanel = !activeSubItem ? SECTION_MAP[activeSection] : null;
  const ActivePanel = SubPanel || SectionPanel;

  return (
    <AppShell>
      <style>{`
        .admin-panel { color: #1e3a5f; }
        .admin-panel .text-muted-foreground { color: #475569 !important; }
        .admin-panel label, .admin-panel p, .admin-panel span, .admin-panel td, .admin-panel th { color: inherit; }
        .admin-panel h1, .admin-panel h2, .admin-panel h3, .admin-panel h4 { color: #1e3a5f; }
      `}</style>
      <div className="admin-panel flex h-[calc(100vh-64px)]">
        {/* Left Sidebar Navigation */}
        <div className="w-56 border-r bg-muted/20 overflow-y-auto flex-shrink-0">
          <nav className="p-2">
            {ADMIN_SECTIONS.map(section => {
              // Hide sections for modules not licensed
              if (section.module) {
                const mods = systemSettings?.modules || systemSettings?.enabled_modules || [];
                if (!mods.includes(section.module)) return null;
              }
              const Icon = section.icon;
              const isExpanded = expandedSections.includes(section.id);
              const isActive = activeSection === section.id;
              
              return (
                <div key={section.id} className="mb-1">
                  <button
                    onClick={() => {
                      if (section.subItems.length > 0) {
                        toggleSection(section.id);
                        setActiveSection(section.id);
                        if (!isExpanded && section.subItems[0]) {
                          setActiveSubItem(section.subItems[0].id);
                        }
                      } else {
                        setActiveSection(section.id);
                        setActiveSubItem(null);
                      }
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-accent text-slate-700'
                    } ${section.bold ? 'font-bold' : ''}`}
                    data-testid={`admin-nav-${section.id}`}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="flex-1 text-left truncate">{section.label}</span>
                    {section.subItems.length > 0 && (
                      <ChevronRight className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    )}
                  </button>
                  
                  {isExpanded && section.subItems.length > 0 && (
                    <div className="ml-6 mt-1 space-y-1">
                      {section.subItems.map(sub => (
                        <button
                          key={sub.id}
                          onClick={() => handleSubItemClick(section.id, sub.id)}
                          className={`w-full text-left px-3 py-1.5 rounded text-xs transition-colors ${
                            activeSubItem === sub.id 
                              ? 'bg-primary text-white font-medium' 
                              : 'hover:bg-accent text-slate-600'
                          }`}
                          data-testid={`admin-nav-${sub.id}`}
                        >
                          {sub.label}
                          {sub.id === 'user-register' && regRequestCount > 0 && (
                            <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{regRequestCount}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
          
          <div className="absolute bottom-0 left-0 w-56 p-3 border-t bg-muted/30 text-xs text-muted-foreground">
            System Currency: <span className="font-medium">USD</span>
          </div>
        </div>
        
        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto p-6">
          {ActivePanel ? <ActivePanel /> : (
            <div className="flex items-center justify-center h-full">
              <EmptyState 
                title={`${ADMIN_SECTIONS.find(s => s.id === activeSection)?.label || 'Section'} Coming Soon`}
                description="This administration section is under development."
              />
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
